import { BooleanInput } from '@angular/cdk/coercion';
import {
  CdkStep,
  CdkStepHeader,
  CdkStepper,
  StepControl,
} from '@angular/cdk/stepper';
import { NgTemplateOutlet } from '@angular/common';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  QueryList,
  ViewChildren,
  booleanAttribute,
  computed,
  input,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControlStatus } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { Observable, of, startWith, switchMap, takeUntil } from 'rxjs';
import { WizardStep } from './wizard-step/wizard-step.component';

@Component({
  selector: 'procure-wizard',
  templateUrl: './wizard.component.html',
  styleUrl: './wizard.component.scss',
  imports: [NgTemplateOutlet, CdkStepHeader, MatIcon],
  providers: [{ provide: CdkStepper, useExisting: Wizard }],
  host: { '[class.vertical]': "orientation === 'vertical'" },
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'procureWizard',
})
export class Wizard extends CdkStepper implements AfterContentInit {
  @ContentChildren(WizardStep, { descendants: true })
  override _steps!: QueryList<WizardStep>;
  @ViewChildren(CdkStepHeader) override _stepHeader!: QueryList<CdkStepHeader>;

  override readonly steps = new QueryList<WizardStep>();

  readonly showStepNumber = input<boolean, BooleanInput>(false, {
    transform: booleanAttribute,
  });

  readonly isFirst = computed(() => this.selectedIndex === 0);
  readonly isLast = computed(() => this.selectedIndex === this.stepCount() - 1);
  readonly canProceed = computed(() => {
    const step = this.selectedStep();

    if (!step) {
      return true;
    }

    const completedOverride = step.completedOverride();

    if (completedOverride !== null) {
      return completedOverride;
    }

    if (step.optional) {
      return true;
    }

    const control = step.control();

    if (control instanceof AbstractControl) {
      const status = this.controlStatus();

      return status !== 'INVALID' && status !== 'PENDING';
    }

    if (control) {
      const state = control();

      return !state.invalid() && !state.pending();
    }

    return true;
  });

  private readonly stepCount = signal(0);
  private readonly selectedStep = computed(() => {
    this.stepCount();

    return this.steps.get(this.selectedIndex);
  });
  private readonly controlStatus = toSignal(
    toObservable(computed(() => this.selectedStep()?.control())).pipe(
      switchMap((control) => this.statusOf(control)),
    ),
    { initialValue: 'VALID' as FormControlStatus },
  );

  override ngAfterContentInit(): void {
    super.ngAfterContentInit();

    this.steps.changes
      .pipe(startWith(null), takeUntil(this._destroyed))
      .subscribe(() => this.stepCount.set(this.steps.length));
  }

  revealErrors(): void {
    const control = this.selectedStep()?.control();

    if (control instanceof AbstractControl) {
      control.markAllAsTouched();
    } else if (control) {
      control().markAsTouched();
    }
  }

  protected selectStep(step: CdkStep): void {
    if (step.isNavigable()) {
      step.select();
    }
  }

  private statusOf(
    control: StepControl | undefined,
  ): Observable<FormControlStatus> {
    if (!(control instanceof AbstractControl)) {
      return of('VALID');
    }

    return control.statusChanges.pipe(startWith(control.status));
  }
}
